import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Wrench, Trash2, Building2, Clock, User, Calendar, Image } from 'lucide-react';
import { PhotoUploader, LazyPhotoGallery } from '../../components/PhotoComponents';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORK_TYPES = [
  { value: 'preventivo', label: 'Preventivo', color: 'emerald' },
  { value: 'correctivo', label: 'Correctivo', color: 'amber' },
  { value: 'averia', label: 'Avería', color: 'rose' },
];

export default function AdminWorks() {
  const [works, setWorks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    company_id: '',
    machine_id: '',
    work_type: 'preventivo',
    description: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '10:00',
    technician: '',
    photos: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [worksRes, companiesRes, machinesRes] = await Promise.all([
        axios.get(`${API}/works`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/machines`)
      ]);
      setWorks(worksRes.data);
      setCompanies(companiesRes.data);
      setMachines(machinesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id || !formData.description || !formData.technician) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      await axios.post(`${API}/works`, {
        ...formData,
        machine_id: formData.machine_id || null
      });
      toast.success('Trabajo registrado');
      setDialogOpen(false);
      setFormData({
        company_id: '',
        machine_id: '',
        work_type: 'preventivo',
        description: '',
        date: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '10:00',
        technician: '',
        photos: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este trabajo?')) return;
    try {
      await axios.delete(`${API}/works/${id}`);
      toast.success('Trabajo eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getTypeBadge = (type) => {
    const option = WORK_TYPES.find(o => o.value === type);
    const colors = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      rose: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${colors[option?.color || 'emerald']}`}>
        {option?.label || type}
      </span>
    );
  };

  const filteredMachines = machines.filter(m => m.company_id === formData.company_id);

  return (
    <Layout title="Registro de Trabajos">
      <div className="space-y-6 animate-fade-in" data-testid="admin-works">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-slate-400">Gestiona los trabajos realizados en cada empresa</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-500 text-white gap-2" data-testid="add-work-btn">
                <Plus className="w-4 h-4" />
                Nuevo Trabajo
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-xl">Registrar Trabajo</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Empresa *</Label>
                    <Select
                      value={formData.company_id || undefined}
                      onValueChange={(v) => setFormData({ ...formData, company_id: v, machine_id: '' })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="work-company-select">
                        <SelectValue placeholder="Selecciona" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {companies.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Máquina</Label>
                    <Select
                      value={formData.machine_id || undefined}
                      onValueChange={(v) => setFormData({ ...formData, machine_id: v === 'none' ? '' : v })}
                      disabled={!formData.company_id}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="work-machine-select">
                        <SelectValue placeholder="Opcional" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">Sin máquina específica</SelectItem>
                        {filteredMachines.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Tipo *</Label>
                    <Select
                      value={formData.work_type}
                      onValueChange={(v) => setFormData({ ...formData, work_type: v })}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white" data-testid="work-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {WORK_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Técnico *</Label>
                    <Input
                      value={formData.technician}
                      onChange={(e) => setFormData({ ...formData, technician: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      placeholder="Nombre del técnico"
                      data-testid="work-technician-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Fecha *</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="work-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Inicio</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white font-mono"
                      data-testid="work-start-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Fin</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white font-mono"
                      data-testid="work-end-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Descripción del Trabajo *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-slate-800 border-slate-700 text-white min-h-[100px]"
                    placeholder="Describe el trabajo realizado..."
                    data-testid="work-description-input"
                  />
                </div>
                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label className="text-slate-300 flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Fotos del Trabajo
                  </Label>
                  <PhotoUploader
                    photos={formData.photos}
                    onChange={(photos) => setFormData({ ...formData, photos })}
                    maxPhotos={5}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400">
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-sky-600 hover:bg-sky-500" data-testid="save-work-btn">
                    Registrar Trabajo
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Works List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : works.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay trabajos registrados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {works.map((work) => (
              <Card key={work.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getTypeBadge(work.work_type)}
                        <span className="text-slate-500">•</span>
                        <span className="text-sm text-slate-400 flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {work.company_name}
                        </span>
                        {work.machine_name && (
                          <>
                            <span className="text-slate-500">•</span>
                            <span className="text-sm text-sky-400">{work.machine_name}</span>
                          </>
                        )}
                      </div>
                      <p className="text-white mb-3">{work.description}</p>
                      {/* Photos */}
                      {work.photo_count > 0 && (
                        <div className="mb-3">
                          <LazyPhotoGallery id={work.id} type="works" count={work.photo_count} />
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(work.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </span>
                        <span className="flex items-center gap-1 font-mono">
                          <Clock className="w-4 h-4" />
                          {work.start_time} - {work.end_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {work.technician}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(work.id)}
                      className="text-slate-400 hover:text-rose-400"
                      data-testid={`delete-work-${work.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
